import React from "react";
import moment from "moment-timezone";
import { deriveOverallStatus } from "../../services/waiverApi";
import "./EsignStatusCard.css";

// frontend/src/components/staff/EsignStatusCard.js
//
// ESIGN-UX-REDESIGN-001 — Story 2 (status indicator), Surface 3: staff
// customer-detail page (light-theme). Renders between the vitals strip and
// the tabs on /staff/customers/:customerId.
//
// Spec sources (locked):
//   docs/design/ESIGN-UX-REDESIGN-001-status-indicator-design.md (Zuri)
//     §5 — placement + 4 visual states (signed / partial / not-signed / revoked)
//     §5.3 — loading + error
//     §5.4 — privacy posture: NO PDF / NO signature image / NO IP / NO envelope ID
//     §5.5 — accessibility
//   docs/architecture/ESIGN-UX-REDESIGN-001-tech.md (Marcus)
//     §Story 2 — esignStatus block (consumed via /api/staff/customers/:id)
//     §Story 2 — privacy guardrail
//
// Privacy posture (DO NOT REGRESS):
//   The card displays status flags + timestamps + form labels ONLY. It MUST
//   NOT add: signed PDF preview, signature image, IP address, SignWell
//   envelope ID, customer DOB, or any other PHI affordance. Future polish
//   that drifts toward "view signed PDF" routes through a higher-permission
//   flow — not this surface. Marcus + Zuri have both flagged this guardrail
//   as load-bearing for HIPAA + breach-surface limitation.
//
// Production-safety:
//   - Pure-presentational; no API calls.
//   - Renders nothing if `esignStatus` is missing from props (graceful
//     fallback while backend is mid-deploy of the additive contract).
//   - Reuses VitalCard chrome conventions per CUST-DETAIL-POLISH-002.

const formatTimestamp = (iso, prefix) => {
  if (!iso) return "";
  const m = moment.tz(iso, "America/Phoenix");
  if (!m.isValid()) return "";
  return `${prefix} ${m.format("MMM D, YYYY HH:mm")} MST`;
};

/**
 * One row of the card: form-name label on the left, status icon + status
 * text on the right. Variants: signed / not-signed / revoked.
 */
const StatusRow = ({ label, signed, signedAt, revokedAt }) => {
  let icon;
  let text;
  let iconClass;

  if (revokedAt) {
    iconClass = "esign-status-card__icon--revoked";
    icon = (
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
        <circle cx="8" cy="8" r="7" fill="currentColor" />
        <path
          d="M5.5 5.5l5 5M10.5 5.5l-5 5"
          stroke="#ffffff"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
    text = formatTimestamp(revokedAt, "Revoked");
  } else if (signed && signedAt) {
    iconClass = "esign-status-card__icon--signed";
    icon = (
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
        <circle cx="8" cy="8" r="7" fill="currentColor" />
        <path
          d="M5 8l2.2 2.2L11 6"
          stroke="#ffffff"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    );
    text = formatTimestamp(signedAt, "Signed");
  } else {
    iconClass = "esign-status-card__icon--warn";
    icon = (
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
        <path
          d="M8 1.5l7 13H1L8 1.5z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <path
          d="M8 6v3.5"
          stroke="#ffffff"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="8" cy="11.5" r="0.8" fill="#ffffff" />
      </svg>
    );
    text = "Not signed";
  }

  return (
    <div className="esign-status-card__row">
      <span className="esign-status-card__row-label">{label}</span>
      <span className={`esign-status-card__icon ${iconClass}`} aria-hidden="true">
        {icon}
      </span>
      <span className="esign-status-card__row-status">{text}</span>
    </div>
  );
};

/**
 * Staff customer-detail ESIGN status card.
 *
 * @param {object} props
 * @param {object} [props.esignStatus] — see EsignStatus type in tech doc.
 *   Omitted/null/undefined: graceful fallback (the card renders an
 *   error/placeholder state per Zuri spec §5.3 if `loading` is false but
 *   `esignStatus` is missing AND `error` is true; otherwise renders null
 *   so the page doesn't carry an empty placeholder while the additive
 *   contract is mid-deploy).
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.error=false] — render error placeholder per §5.3
 */
const EsignStatusCard = ({ esignStatus, loading = false, error = false }) => {
  if (loading) {
    return (
      <section
        className="esign-status-card esign-status-card--loading"
        aria-label="ESIGN compliance status for this customer"
        aria-busy="true"
        role="status"
      >
        <div className="esign-status-card__label-skeleton" />
        <div className="esign-status-card__row-skeleton" />
        <div className="esign-status-card__row-skeleton" />
      </section>
    );
  }

  if (error || (!esignStatus && error)) {
    return (
      <section
        className="esign-status-card esign-status-card--error"
        aria-label="ESIGN compliance status for this customer"
      >
        <span className="esign-status-card__label">ESIGN STATUS</span>
        <div className="esign-status-card__row">
          <span className="esign-status-card__row-label">Consent</span>
          <span className="esign-status-card__row-status">—</span>
        </div>
        <div className="esign-status-card__row">
          <span className="esign-status-card__row-label">Release</span>
          <span className="esign-status-card__row-status">—</span>
        </div>
        <p className="esign-status-card__error-helper">
          Couldn't load. Refresh to try again.
        </p>
      </section>
    );
  }

  if (!esignStatus) {
    // Backend not yet deployed with the additive contract — fail-quiet.
    return null;
  }

  const overall = deriveOverallStatus(esignStatus);

  return (
    <section
      className={`esign-status-card esign-status-card--${overall}`}
      aria-label="ESIGN compliance status for this customer"
      data-testid="esign-status-card"
    >
      <span className="esign-status-card__label">ESIGN STATUS</span>
      <StatusRow
        label="Consent"
        signed={Boolean(esignStatus.consentSigned)}
        signedAt={esignStatus.consentSignedAt}
        revokedAt={esignStatus.consentRevokedAt}
      />
      <StatusRow
        label="Release"
        signed={Boolean(esignStatus.releaseSigned)}
        signedAt={esignStatus.releaseSignedAt}
        revokedAt={esignStatus.releaseRevokedAt}
      />
    </section>
  );
};

export default EsignStatusCard;
